import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, MessageCircle, Sparkles } from "lucide-react";
import { COMMUNITY_TABLE, ENTREPRENEUR_WORKSHOP } from "@/data/media";

const PASSI = [
  {
    icon: MessageCircle,
    titolo: "1. Ci racconti la tua storia",
    testo:
      "Compili un modulo semplice: chi sei, cosa fai e cosa ti ha portata fino a qui. Ti servono due minuti, è gratuito.",
  },
  {
    icon: Sparkles,
    titolo: "2. Ti ricontattiamo noi",
    testo:
      "Leggiamo il tuo racconto e ti scriviamo per approfondirlo insieme. Decidi tu cosa raccontare e cosa tenere per te.",
  },
  {
    icon: HeartHandshake,
    titolo: "3. La tua storia entra nella rete",
    testo:
      "Pubblichiamo la tua storia solo dopo la tua approvazione, sul sito e su Instagram, e ti presentiamo alle altre imprenditrici.",
  },
];

export const HowItWorks = () => (
  <section
    className="mx-auto max-w-[1200px] px-5 py-16 sm:px-10 sm:py-20"
    data-testid="how-it-works"
    aria-label="Come funziona"
  >
    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E0708A]">
      Come funziona
    </p>
    <h2 className="mt-4 max-w-2xl font-display text-3xl leading-tight tracking-tight text-[#1A1617] sm:text-4xl">
      Tre passi, nessun costo,
      <br />
      <span className="di-shine italic">nessun impegno</span>.
    </h2>

    <div className="mt-12 grid gap-5 md:grid-cols-3">
      {PASSI.map((p, i) => (
        <motion.div
          key={p.titolo}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, delay: i * 0.1 }}
          data-testid={`step-${i + 1}`}
          className="rounded-[24px] border border-[#E8E2E4] bg-white p-7 shadow-[0_8px_32px_rgba(94,42,82,0.05)]"
        >
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#FAF5F6] text-[#E0708A]">
            <p.icon size={18} />
          </span>
          <h3 className="mt-5 font-display text-xl text-[#1A1617]">{p.titolo}</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#6B5E62]">{p.testo}</p>
        </motion.div>
      ))}
    </div>

    <div className="mt-10">
      <Link
        to="/racconta-la-tua-storia"
        data-testid="how-it-works-cta"
        className="group inline-flex items-center gap-2 rounded-full bg-[#5E2A52] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#E0708A]"
      >
        Inizia: racconta la tua storia
        <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </div>
  </section>
);

export const TogetherStrong = () => (
  <section
    className="mx-auto max-w-[1400px] px-5 py-16 sm:px-10 sm:py-20"
    data-testid="together-strong"
    aria-label="Perché insieme"
  >
    <div className="grid items-center gap-10 lg:grid-cols-2">
      <motion.div
        initial={{ opacity: 0, x: -24 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7 }}
        className="overflow-hidden rounded-[28px]"
      >
        <img
          src={COMMUNITY_TABLE}
          alt="Imprenditrici che si confrontano intorno a un tavolo"
          className="block h-auto w-full"
          loading="lazy"
          data-testid="together-image"
        />
      </motion.div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E0708A]">
          Perché insieme
        </p>
        <h2 className="mt-4 font-display text-3xl leading-tight tracking-tight text-[#1A1617] sm:text-4xl">
          Da sola arrivi prima.
          <br />
          <span className="di-shine italic">Insieme arrivi più lontano</span>.
        </h2>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-[#6B5E62]">
          Chi fa impresa da sola conosce bene quel momento in cui non sai a chi
          chiedere. DonnaImpresa nasce per quel momento: una rete di donne in
          impresa che si passano contatti, clienti, errori già fatti e soluzioni
          già trovate.
        </p>

        <ul className="mt-8 space-y-4" data-testid="together-points">
          {[
            ["Ti fai conoscere", "La tua storia raggiunge chi non sapeva che esistessi: clienti, colleghe, possibili collaborazioni."],
            ["Non parti da zero", "Le imprenditrici con più anni di attività condividono quello che hanno imparato, senza formalità."],
            ["Fate forza insieme", "Più siamo, più peso ha la voce delle imprese guidate da donne: fornitori, bandi, visibilità."],
          ].map(([t, d]) => (
            <li key={t} className="flex gap-4">
              <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#E0708A]" />
              <p className="text-sm leading-relaxed text-[#6B5E62]">
                <strong className="text-[#1A1617]">{t}.</strong> {d}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

export const StoryInvite = () => (
  <section
    className="mx-auto max-w-[1400px] px-5 pb-20 pt-14 sm:px-10 sm:pt-16"
    data-testid="story-invite"
    aria-label="Invito a raccontare la propria storia"
  >
    <div className="grid items-stretch gap-0 overflow-hidden rounded-[32px] bg-[#5E2A52] lg:grid-cols-2">
      <div className="flex flex-col justify-center p-8 sm:p-14">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E0708A]">
          Tocca a te
        </p>
        <h2 className="mt-5 font-display text-3xl leading-tight text-white sm:text-4xl">
          C'è una storia che nessuno
          <br />
          ha ancora raccontato: la tua.
        </h2>
        <p className="mt-6 max-w-lg text-sm leading-relaxed text-white/75">
          Non serve avere un'azienda grande, né un bilancio da esibire. Serve
          avere iniziato. Raccontaci com'è andata: la leggiamo davvero, una per
          una, e ti rispondiamo.
        </p>
        <Link
          to="/racconta-la-tua-storia"
          data-testid="story-invite-cta"
          className="group mt-9 inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-semibold text-[#5E2A52] transition-colors duration-300 hover:bg-[#E0708A] hover:text-white"
        >
          Racconta la tua storia
          <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
        <p className="mt-4 text-xs text-white/50">
          Due minuti · gratuito · pubblichiamo solo con il tuo ok
        </p>
      </div>
      <img
        src={ENTREPRENEUR_WORKSHOP}
        alt="Imprenditrice al lavoro nel suo laboratorio artigianale"
        className="h-full max-h-[440px] min-h-[260px] w-full object-cover object-[center_22%]"
        loading="lazy"
        data-testid="story-invite-image"
      />
    </div>
  </section>
);
